import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown, Edit, Trash2, Search, Filter, Copy, Send, Receipt } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";

const TYPE_LABEL = {
  flight: "Flight",
  hotel: "Hotel",
  domestic_tour: "Domestic Tour",
  international_tour: "International Tour",
};

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  partially_paid: "bg-sky-100 text-sky-800",
  cancelled: "bg-red-100 text-red-800",
  refunded: "bg-red-100 text-red-800",
};

export default function Invoices() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    invoice_type: "", status: "", gst_mode: "", destination: "", sales_executive: "",
    invoice_date_from: "", invoice_date_to: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/invoices", { params });
      setItems(data);
    } catch { toast.error("Could not load invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, filters]);

  const openPdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Failed to open PDF"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Deleted"); load();
  };
  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/duplicate`);
      toast.success(`Duplicated as ${data.invoice_number}`);
      nav(`/invoices/${data.id}/edit`);
    } catch { toast.error("Could not duplicate"); }
  };

  const totalAmount = useMemo(() => items.reduce((s, x) => s + (x?.summary?.grand_total || 0), 0), [items]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-12">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline text-[10px] tracking-[0.28em] text-emerald-700">INVOICES</div>
            <h1 className="font-editorial text-3xl text-slate-900 mt-1">Client billing</h1>
            <p className="text-sm text-slate-500 mt-1">Create, track and share GST-compliant invoices for every booking.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="inv-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Invoice #, customer, PNR, destination…"
                className="w-72 border border-border rounded pl-9 pr-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <button
              data-testid="inv-filter-toggle"
              onClick={() => setShowFilters((s) => !s)}
              className={`inline-flex items-center gap-2 border rounded px-3 py-2 text-sm ${showFilters ? "border-emerald-500 text-emerald-700" : "border-border text-slate-600"}`}
            >
              <Filter size={14} /> Filters
            </button>
            <button
              data-testid="inv-new-btn"
              onClick={() => nav("/invoices/new")}
              className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 text-white px-4 py-2.5 rounded text-sm font-semibold"
            >
              <Plus size={15} /> New Invoice
            </button>
          </div>
        </div>

        {showFilters && (
          <div data-testid="inv-filter-panel" className="bg-white border border-border rounded p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select label="Type" testid="f-type" value={filters.invoice_type} onChange={(v) => setF("invoice_type", v)}
              options={[["", "All types"], ["flight", "Flight"], ["hotel", "Hotel"], ["domestic_tour", "Domestic Tour"], ["international_tour", "International Tour"]]} />
            <Select label="Status" testid="f-status" value={filters.status} onChange={(v) => setF("status", v)}
              options={[["", "All statuses"], ["draft", "Draft"], ["pending", "Pending"], ["paid", "Paid"], ["partially_paid", "Partially Paid"], ["cancelled", "Cancelled"], ["refunded", "Refunded"]]} />
            <Select label="GST Mode" testid="f-gst" value={filters.gst_mode} onChange={(v) => setF("gst_mode", v)}
              options={[["", "Any"], ["none", "No GST"], ["service_18", "18% on Services"], ["package_5", "5% on Package"]]} />
            <TextField label="Destination" testid="f-dest" value={filters.destination} onChange={(v) => setF("destination", v)} placeholder="e.g. Bali" />
            <TextField label="Sales Executive" testid="f-exec" value={filters.sales_executive} onChange={(v) => setF("sales_executive", v)} placeholder="e.g. Riya" />
            <TextField type="date" label="Invoice Date ≥" testid="f-idate-from" value={filters.invoice_date_from} onChange={(v) => setF("invoice_date_from", v)} />
            <TextField type="date" label="Invoice Date ≤" testid="f-idate-to" value={filters.invoice_date_to} onChange={(v) => setF("invoice_date_to", v)} />
            <div className="flex items-end">
              <button onClick={() => setFilters({ invoice_type: "", status: "", gst_mode: "", destination: "", sales_executive: "", invoice_date_from: "", invoice_date_to: "" })} className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-4">Clear all</button>
            </div>
          </div>
        )}

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="Total invoices" value={items.length} />
          <Stat label="Amount billed" value={`₹ ${totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} />
          <Stat label="Paid" value={items.filter((x) => x.status === "paid").length} />
          <Stat label="Pending" value={items.filter((x) => x.status === "pending" || x.status === "draft").length} />
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center">
            <Receipt size={32} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500 mb-4">No invoices yet. Bill your first client to see it here.</div>
            <button data-testid="inv-first-btn" onClick={() => nav("/invoices/new")} className="inline-flex items-center gap-2 text-sm text-[#16A34A] hover:underline">
              <Plus size={14} /> Create invoice
            </button>
          </div>
        ) : (
          <div className="bg-white border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-500 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">Invoice #</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Destination</th>
                  <th className="text-left px-4 py-3">Invoice Date</th>
                  <th className="text-right px-4 py-3">Grand Total</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} data-testid={`inv-row-${v.id}`} className="border-t border-border hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link className="hover:underline text-slate-900" to={`/invoices/${v.id}`}>{v.invoice_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{TYPE_LABEL[v.invoice_type] || v.invoice_type}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{v.customer?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.booking?.destination || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.booking?.invoice_date || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">₹ {(v.summary?.grand_total || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10.5px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest ${STATUS_STYLE[v.status] || "bg-slate-100 text-slate-700"}`}>
                        {(v.status || "draft").replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`inv-pdf-${v.id}`} onClick={() => openPdf(v.id)} className="p-1.5 text-slate-500 hover:text-[#16A34A]" title="Download PDF"><FileDown size={15} /></button>
                        <Link data-testid={`inv-edit-${v.id}`} to={`/invoices/${v.id}/edit`} className="p-1.5 text-slate-500 hover:text-blue-600" title="Edit"><Edit size={15} /></Link>
                        <button data-testid={`inv-dup-${v.id}`} onClick={() => duplicate(v.id)} className="p-1.5 text-slate-500 hover:text-indigo-600" title="Duplicate"><Copy size={15} /></button>
                        <Link data-testid={`inv-share-${v.id}`} to={`/invoices/${v.id}`} className="p-1.5 text-slate-500 hover:text-teal-600" title="View & Share"><Send size={15} /></Link>
                        <button data-testid={`inv-del-${v.id}`} onClick={() => remove(v.id)} className="p-1.5 text-slate-500 hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-border rounded p-4">
      <div className="overline">{label}</div>
      <div className="font-editorial text-2xl mt-2">{value}</div>
    </div>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <label className="text-xs">
      <div className="uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-border rounded px-2 py-2 text-sm bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function TextField({ label, testid, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="text-xs">
      <div className="uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-border rounded px-2 py-2 text-sm" />
    </label>
  );
}
